import { Button, DatePicker, Form, Input, Select, Table, Tag, Typography } from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { transportApi, type AuditEntryDto } from "../../api/transportApi";
import { getApiErrorMessage } from "../../utils/apiError";

const { Title } = Typography;
const { RangePicker } = DatePicker;

export function AuditLogPage() {
  const [form]    = Form.useForm();
  const [rows,   setRows]    = useState<AuditEntryDto[]>([]);
  const [total,  setTotal]   = useState(0);
  const [page,   setPage]    = useState(1);
  const [loading,setLoading] = useState(false);
  const [error,  setError]   = useState<string | null>(null);
  const [branchOptions, setBranchOptions] = useState<Array<{ value: number; label: string }>>([]);
  const [userOptions, setUserOptions] = useState<Array<{ value: string; label: string }>>([]);

  const PAGE_SIZE = 50;

  const load = async (p = 1) => {
    setLoading(true);
    setError(null);
    try {
      const values = form.getFieldsValue();
      const [from, to] = (values.dateRange as [dayjs.Dayjs?, dayjs.Dayjs?]) ?? [];
      const result = await transportApi.getAuditLogs({
        page:       p,
        pageSize:   PAGE_SIZE,
        branchId:   values.branchId ? Number(values.branchId) : undefined,
        userId:     values.userId    || undefined,
        entityType: values.entityType || undefined,
        action:     values.action    || undefined,
        dateFrom:   from ? from.toISOString() : undefined,
        dateTo:     to   ? to.toISOString()   : undefined,
      });
      setRows(result.data);
      setTotal(result.total);
      setPage(p);
    } catch (err) {
      setError(getApiErrorMessage(err, "Failed to load audit logs"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(1); }, []);
  useEffect(() => {
    void Promise.all([
      transportApi.searchBranchLookups("", 1, 200),
      transportApi.listAdminUsers({ page: 1, pageSize: 200 })
    ]).then(([branches, users]) => {
      setBranchOptions(branches.items.map((item) => ({ value: item.id, label: item.label })));
      setUserOptions(users.map((user: any) => ({
        value: String(user.id),
        label: user.displayName || [user.firstName, user.lastName].filter(Boolean).join(" ") || user.email || user.userName
      })));
    }).catch(() => undefined);
  }, []);

  const actionColor = (a: string) =>
    a === "Create" ? "green" : a === "Delete" ? "red" : a === "SoftDelete" ? "orange" : "blue";

  return (
    <div style={{ padding: 16 }}>
      <Title level={4} style={{ margin: "0 0 12px" }}>Audit Log</Title>

      <Form form={form} layout="inline" style={{ marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
        <Form.Item name="dateRange" label="Date">
          <RangePicker format="DD/MM/YYYY" />
        </Form.Item>
        <Form.Item name="branchId" label="Branch">
          <Select allowClear showSearch optionFilterProp="label" placeholder="Select branch" options={branchOptions} style={{ width: 190 }} />
        </Form.Item>
        <Form.Item name="userId" label="User">
          <Select allowClear showSearch optionFilterProp="label" placeholder="Select user" options={userOptions} style={{ width: 190 }} />
        </Form.Item>
        <Form.Item name="entityType" label="Entity">
          <Input placeholder="e.g. GatePass" style={{ width: 130 }} />
        </Form.Item>
        <Form.Item name="action" label="Action">
          <Select allowClear placeholder="Any" style={{ width: 120 }}>
            <Select.Option value="Create">Create</Select.Option>
            <Select.Option value="Update">Update</Select.Option>
            <Select.Option value="Delete">Delete</Select.Option>
            <Select.Option value="SoftDelete">SoftDelete</Select.Option>
          </Select>
        </Form.Item>
        <Form.Item>
          <Button type="primary" onClick={() => void load(1)}>Search</Button>
          <Button style={{ marginLeft: 8 }} onClick={() => { form.resetFields(); void load(1); }}>Reset</Button>
        </Form.Item>
      </Form>

      {error && <div style={{ color: "red", marginBottom: 8 }}>{error}</div>}

      <Table<AuditEntryDto>
        rowKey="id"
        size="small"
        loading={loading}
        dataSource={rows}
        scroll={{ x: 1000 }}
        pagination={{
          current:   page,
          pageSize:  PAGE_SIZE,
          total,
          onChange:  p => void load(p),
          showTotal: t => `${t} entries`,
        }}
        columns={[
          {
            title: "Timestamp",
            dataIndex: "timestamp",
            width: 160,
            render: (v: string) => dayjs(v).format("DD/MM/YYYY HH:mm:ss"),
          },
          { title: "Action",    dataIndex: "action",    width: 90,  render: (v: string) => <Tag color={actionColor(v)}>{v}</Tag> },
          { title: "Entity",    dataIndex: "entityType", width: 130 },
          { title: "Entity ID", dataIndex: "entityId",   width: 90  },
          { title: "User",      dataIndex: "userName",   width: 150 },
          { title: "Branch",    dataIndex: "branchName", width: 160, render: (v?: string) => v || "—" },
          { title: "IP",        dataIndex: "ipAddress",  width: 120, render: (v?: string) => v ?? "—" },
          {
            title: "Changed Fields",
            dataIndex: "changedFields",
            render: (v?: string) => {
              if (!v) return "—";
              try {
                const fields = JSON.parse(v) as { field: string }[];
                return fields.map(f => f.field).join(", ");
              } catch {
                return v;
              }
            },
          },
        ]}
      />
    </div>
  );
}
